import { useState, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { 
  User, Mail, Phone, Briefcase, Send, Loader2, CheckCircle,
  MapPin, Clock, Upload, FileText, X, Shield
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostulerPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ nom: "", prenom: "", email: "", telephone: "", message: "" });
  const [files, setFiles] = useState({ cv: null, lettreMotivation: null, carteIdentite: null, photo: null, planActiva: null });
  const [rgpdConsent, setRgpdConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRefs = {
    cv: useRef(null), lettreMotivation: useRef(null), carteIdentite: useRef(null), photo: useRef(null), planActiva: useRef(null)
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Fichier trop volumineux", { description: "Max 5 Mo" });
        return;
      }
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format non supporté", { description: "PDF, JPG, PNG, DOC, DOCX" });
        return;
      }
      setFiles(prev => ({ ...prev, [fileType]: file }));
    }
  };

  const removeFile = (fileType) => {
    setFiles(prev => ({ ...prev, [fileType]: null }));
    if (fileInputRefs[fileType].current) fileInputRefs[fileType].current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rgpdConsent) {
      toast.error("Consentement requis");
      return;
    }
    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('nom', formData.nom);
      submitData.append('prenom', formData.prenom);
      submitData.append('email', formData.email);
      submitData.append('telephone', formData.telephone);
      submitData.append('message', formData.message || '');
      submitData.append('rgpdConsent', 'true');
      submitData.append('consentDate', new Date().toISOString());
      Object.keys(files).forEach(key => { if (files[key]) submitData.append(key, files[key]); });
      await axios.post(`${API}/candidature`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setIsSubmitted(true);
      toast.success(t('candidatureEnvoyee'));
      setFormData({ nom: "", prenom: "", email: "", telephone: "", message: "" });
      setFiles({ cv: null, lettreMotivation: null, carteIdentite: null, photo: null, planActiva: null });
      setRgpdConsent(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileFields = [
    { key: 'cv', label: t('cv') },
    { key: 'lettreMotivation', label: t('lettreMotivation') },
    { key: 'carteIdentite', label: t('carteIdentite') },
    { key: 'photo', label: t('photoIdentite') },
    { key: 'planActiva', label: t('planActiva') }
  ];

  const avantages = [
    { icon: Clock, title: t('horairesFlexibles'), description: t('horairesFlexiblesDesc') },
    { icon: Briefcase, title: t('formationContinueAvantage'), description: t('formationContinueAvantageDesc') },
    { icon: User, title: t('equipeBienveillante'), description: t('equipeBienveillanteDesc') },
    { icon: MapPin, title: t('proximite'), description: t('proximiteDesc') }
  ];

  return (
    <div data-testid="postuler-page" className="pt-20">
      <SEO
        title="Rejoignez notre équipe - Offres d'emploi"
        description="Rejoignez Nettis Facility ! Nous recrutons des agents de nettoyage, chefs d'équipe et responsables. Horaires flexibles, formation continue, environnement bienveillant. Postulez maintenant."
        keywords="emploi nettoyage Belgique, recrutement agent entretien, offre emploi nettoyage Bruxelles, travailler nettoyage, job nettoyage"
        canonicalUrl="https://www.nettis.be/postuler"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('rejoignezNous')}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
              {t('postulerTitle')} <span className="text-[#1E3A8A]">Nettis Facility</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">{t('postulerDesc')}</p>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {avantages.map((avantage) => (
              <div key={avantage.title} className="text-center p-6 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
                  <avantage.icon className="w-6 h-6 text-[#1E3A8A]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">{avantage.title}</h3>
                <p className="text-sm text-slate-500">{avantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="formulaire" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-2xl">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">{t('formulaireCandidature')}</h2>
            <p className="text-slate-600 mb-8">{t('formulaireDesc')}</p>
            
            {isSubmitted ? (
              <div className="bg-green-50 rounded-2xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{t('candidatureEnvoyee')}</h3>
                <p className="text-slate-600 mb-6">{t('merciCandidature')}</p>
                <Button onClick={() => setIsSubmitted(false)} className="bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full px-6">
                  {t('soumettreAutre')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nom">{t('nom')} *</Label>
                    <Input id="nom" name="nom" value={formData.nom} onChange={handleChange} placeholder={t('votreNom')} required className="bg-slate-50 border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prenom">{t('prenom')} *</Label>
                    <Input id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} placeholder={t('votrePrenom')} required className="bg-slate-50 border-slate-200" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')} *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder={t('votreMail')} required className="bg-slate-50 border-slate-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telephone">{t('telephone')} *</Label>
                  <Input id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} placeholder="+32 xxx xx xx xx" required className="bg-slate-50 border-slate-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{t('message')}</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder={t('votreMessageCandidature')} rows={4} className="bg-slate-50 border-slate-200 resize-none" />
                </div>

                {/* Documents Section */}
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Upload className="w-5 h-5 text-[#1E3A8A]" />
                    <h3 className="text-lg font-semibold text-[#0F172A]">{t('documentsOptionnels')}</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">{t('formatsAcceptes')}</p>
                  <div className="space-y-3">
                    {fileFields.map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-sm">{label}</Label>
                            {files[key] && (
                              <button type="button" onClick={() => removeFile(key)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1">
                                <X className="w-3 h-3" />{t('supprimer')}
                              </button>
                            )}
                          </div>
                          <div className="relative">
                            <input type="file" ref={fileInputRefs[key]} onChange={(e) => handleFileChange(e, key)} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" id={`file-${key}`} />
                            <label htmlFor={`file-${key}`} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${files[key] ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                              {files[key] ? (<><FileText className="w-4 h-4" /><span className="text-sm truncate">{files[key].name}</span></>) : (<><Upload className="w-4 h-4" /><span className="text-sm">{t('choisirFichier')}</span></>)}
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RGPD Section */}
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-[#1E3A8A]" />
                    <h3 className="text-lg font-semibold text-[#0F172A]">{t('protectionDonnees')}</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-2">
                    <p><strong>{t('responsableTraitement')}:</strong> Nettis Facility, Avenue Louise 65 boîte 11, 1050 Bruxelles.</p>
                    <p><strong>{t('finalite')}:</strong> {t('finaliteDesc')}</p>
                    <p><strong>{t('conservation')}:</strong> {t('conservationDesc')}</p>
                    <p><strong>{t('vosDroits')}:</strong> {t('vosDroitsDesc')} <a href="mailto:job@nettis.be" className="text-[#1E3A8A] hover:underline">job@nettis.be</a>.</p>
                    <p><strong>{t('reclamation')}:</strong> {t('reclamationDesc')}: <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">www.autoriteprotectiondonnees.be</a></p>
                  </div>
                  <div className="flex items-start gap-3 mt-4">
                    <Checkbox id="rgpdConsent" checked={rgpdConsent} onCheckedChange={(checked) => setRgpdConsent(checked)} className="mt-1" />
                    <label htmlFor="rgpdConsent" className="text-sm text-slate-700 cursor-pointer">{t('consentementRgpd')} *</label>
                  </div>
                </div>

                <Button type="submit" disabled={isSubmitting || !rgpdConsent} className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-full py-3 font-semibold shadow-lg shadow-green-900/20 disabled:opacity-50">
                  {isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('envoiEnCours')}</>) : (<><Send className="mr-2 h-5 w-5" />{t('envoyerCandidature')}</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Contact RH */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">{t('questionRecrutement')}</h2>
          <p className="text-slate-600 mb-6">{t('equipeRhDisposition')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="mailto:job@nettis.be" className="flex items-center gap-2 text-[#1E3A8A] hover:underline">
              <Mail className="w-5 h-5" />job@nettis.be
            </a>
            <span className="hidden sm:block text-slate-300">•</span>
            <a href="tel:+3228806650" className="flex items-center gap-2 text-[#1E3A8A] hover:underline">
              <Phone className="w-5 h-5" />+32 2 880 66 50
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostulerPage;
